import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FileText, Bell } from "lucide-react";
import { CaseTooltip } from './CaseTooltip';

interface Case {
  id: string;
  title: string;
  case_number?: string;
  case_number_cnj?: string;
  case_type?: string;
  status?: string;
  court?: string;
  filing_date?: string;
  clients?: { name: string; email: string };
}

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onTaskCreated: () => void;
}

export const CreateTaskModal = ({
  isOpen,
  onClose,
  selectedDate,
  onTaskCreated
}: CreateTaskModalProps) => {
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const REMINDER_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: "Na hora" },
    { value: 10, label: "10 min antes" },
    { value: 30, label: "30 min antes" },
    { value: 60, label: "1 h antes" },
    { value: 120, label: "2 h antes" },
    { value: 1440, label: "1 dia antes" },
    { value: 2880, label: "2 dias antes" },
  ];

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "Média",
    due_date: format(selectedDate, "yyyy-MM-dd"),
    due_time: "09:00",
    case_id: "",
    push_notify: true,
    reminder_offsets: [0, 60] as number[],
    show_in_portal: false,
  });

  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchCases();
    }
  }, [isOpen]);

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select(`
          id, 
          title, 
          case_number,
          case_number_cnj,
          case_type,
          status,
          court,
          filing_date,
          clients:client_id(id, name, email)
        `)
        .order('title');

      if (error) throw error;
      setCases(data || []);
    } catch (error) {
      console.error('Erro ao buscar processos:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha o título da tarefa.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const dueDateTime = new Date(`${formData.due_date}T${formData.due_time}`);

      const offsets = formData.push_notify
        ? [...new Set(formData.reminder_offsets)].sort((a, b) => a - b)
        : [];

      const { data: newTask, error } = await supabase.from('tasks').insert({
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        due_date: dueDateTime.toISOString(),
        status: 'Pendente',
        user_id: user.id,
        case_id: formData.case_id === "none" ? null : formData.case_id || null,
        show_in_portal: !!formData.show_in_portal && !!formData.case_id && formData.case_id !== "none",
        // Compat: mantemos reminder_date apontando para "na hora" se houver push.
        reminder_date: formData.push_notify ? dueDateTime.toISOString() : null,
        reminder_sent: false,
        reminder_offsets_minutes: offsets,
        reminder_sent_offsets: [],
      } as any).select().single();

      if (error) throw error;

      // Enviar notificação push imediata
      if (newTask && formData.push_notify) {
        try {
          // Verificar se estamos em ambiente local (sem backend serverless)
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          
          if (isLocalhost) {
            console.info('[Push Notification] Ambiente local detectado. API ignorada para evitar erros 500.');
          } else {
            const linkedCaseId = formData.case_id && formData.case_id !== 'none' ? formData.case_id : null;
            await fetch('/api/notifications/notify-on-create', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: linkedCaseId ? 'deadline' : 'task',
                itemId: newTask.id,
                userId: user.id,
                title: formData.title,
                dueDate: dueDateTime.toISOString(),
                caseId: linkedCaseId,
              })
            });
          }
        } catch (notifError) {
          // Não bloquear criação se notificação falhar
          console.warn('Erro ao enviar notificação:', notifError);
        }
      }

      toast({
        title: "Sucesso!",
        description: "Tarefa criada com sucesso."
      });

      onTaskCreated();
      onClose();
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        priority: "Média",
        due_date: format(selectedDate, "yyyy-MM-dd"),
        due_time: "09:00",
        case_id: "",
        push_notify: true,
        reminder_offsets: [0, 60],
        show_in_portal: false,
      });

    } catch (error: any) {
      console.error('Erro ao criar tarefa:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar tarefa",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleReminderOffset = (offset: number) => {
    setFormData(prev => {
      const exists = prev.reminder_offsets.includes(offset);
      const next = exists
        ? prev.reminder_offsets.filter(o => o !== offset)
        : [...prev.reminder_offsets, offset];
      return { ...prev, reminder_offsets: next };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30">
        <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          {/* Monograma AS como marca d'água */}
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-center gap-4 text-left">
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt="Allana Sthéfany"
              className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
            />
            <div className="min-w-0">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Nova Tarefa</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-500">
                <span className="font-medium text-[#7a6440]">
                  {format(selectedDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Ex: Revisar contrato, Preparar petição..."
              required
              className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
            />
          </div>

          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detalhes sobre a tarefa..."
              rows={3}
              className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="due_date">Data</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => handleInputChange('due_date', e.target.value)}
                className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
              />
            </div>
            <div>
              <Label htmlFor="due_time">Horário</Label>
              <Input
                id="due_time"
                type="time"
                value={formData.due_time}
                onChange={(e) => handleInputChange('due_time', e.target.value)}
                className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="priority">Prioridade</Label>
            <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
              <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                <SelectValue placeholder="Selecione a prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Baixa">Baixa</SelectItem>
                <SelectItem value="Média">Média</SelectItem>
                <SelectItem value="Alta">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          </div>

          <div className="space-y-4">
          {/* Toggle de notificação push + opções de quando avisar */}
          <div className="space-y-2 rounded-xl border border-[#c4ab83]/30 bg-[#faf7f2] p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">Notificar por push</p>
                  <p className="text-xs text-muted-foreground">Receber alerta no celular / desktop</p>
                </div>
              </div>
              <Switch
                checked={formData.push_notify}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, push_notify: !!checked }))}
              />
            </div>

            {formData.push_notify && (
              <div className="pt-2 border-t border-primary/10">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Quando me lembrar (pode marcar vários)
                </p>
                <div className="flex flex-wrap gap-2">
                  {REMINDER_OPTIONS.map(opt => {
                    const active = formData.reminder_offsets.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleReminderOffset(opt.value)}
                        className={
                          active
                            ? "px-3 py-1.5 rounded-full text-xs font-medium border bg-primary text-primary-foreground border-primary shadow-sm transition"
                            : "px-3 py-1.5 rounded-full text-xs font-medium border bg-white/40 text-foreground border-primary/30 hover:bg-primary/10 transition"
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {formData.reminder_offsets.length === 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    Sem horários selecionados — só você verá no sino do app.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="case_id">Processo vinculado</Label>
            <Select value={formData.case_id} onValueChange={(value) => handleInputChange('case_id', value)}>
              <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                <SelectValue placeholder="Selecione um processo (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum processo</SelectItem>
                {cases.map((case_) => (
                  <SelectItem key={case_.id} value={case_.id}>
                    <span className="truncate">
                      {case_.title.length > 60 ? `${case_.title.substring(0, 60)}...` : case_.title}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Mostrar informações do processo selecionado */}
            {formData.case_id && formData.case_id !== "none" && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 overflow-hidden">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-blue-800">Processo Vinculado:</span>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700 font-medium">
                    {(() => {
                      const title = cases.find(c => c.id === formData.case_id)?.title || '';
                      return title.length > 60 ? `${title.substring(0, 60)}...` : title;
                    })()}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Número:</span>
                    <CaseTooltip caseData={cases.find(c => c.id === formData.case_id)!}>
                      <button
                        onClick={() => window.location.href = `/processo/${cases.find(c => c.id === formData.case_id)?.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs transition-colors"
                      >
                        {cases.find(c => c.id === formData.case_id)?.case_number_cnj || cases.find(c => c.id === formData.case_id)?.case_number || 'Sem número'}
                      </button>
                    </CaseTooltip>
                  </div>
                </div>
              </div>
            )}
            {formData.case_id && formData.case_id !== "none" && (
              <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-[#c4ab83]/40 bg-[#faf7f2] px-3 py-2.5">
                <Switch checked={!!formData.show_in_portal} onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, show_in_portal: checked }))} className="mt-0.5" />
                <span className="text-sm">
                  <span className="block font-medium text-slate-800">Mostrar ao cliente no portal</span>
                  <span className="block text-xs text-slate-500">Ao concluir, o título desta tarefa entra como andamento do processo no Portal do Cliente e ele recebe um aviso.</span>
                </span>
              </label>
            )}
          </div>

          </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#c4ab83] font-semibold text-slate-900 hover:bg-[#b39a72]">
              {loading ? "Criando..." : "Criar Tarefa"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
